import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Provider } from "react-redux";
import { Toaster } from "react-hot-toast";
import styled, { ThemeProvider, createGlobalStyle } from "styled-components";
import { store } from "./store";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "./store";
import { initializeAuth, logout } from "./store/slices/authSlice";
import { checkStorageHealth } from "./utils/storageUtils";
import { useGetProfileQuery } from "./store/trader-api";
import { useTheme } from "./hooks/useTheme";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Orders from "./pages/Orders";
import OrderDetail from "./pages/OrderDetail";
import Balance from "./pages/Balance";
import NewRequisites from "./pages/NewRequisites";
import Appeals from "./pages/Appeals";
import AppealDetail from "./pages/AppealDetail";
import Settings from "./pages/Settings";
import Layout from "./components/Layout";
import DeviceTestPage from "./components/DeviceTestPage";

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Aeonik Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    background-color: var(--bg-primary);
    color: var(--text-primary);
    line-height: 1.5;
    transition: background-color var(--transition-speed) ease, color var(--transition-speed) ease;
  }

  input, button, textarea, select {
    font-family: inherit;
  }

  button {
    cursor: pointer;
  }

  a {
    text-decoration: none;
    color: inherit;
  }
`;

const theme = {
  colors: {
    primary: "#194BFF",
    primaryHover: "#3970FF",
    secondary: "#64748b",
    success: "#B2B2B2",
    warning: "#194BFF",
    error: "#ef4444",
    background: "#ffffff",
    backgroundSecondary: "#f8fafc",
    border: "#e2e8f0",
    text: "#1e293b",
    textSecondary: "#64748b",
    textMuted: "#94a3b8",
  },
  shadows: {
    sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  },
  borderRadius: {
    sm: "0.375rem",
    md: "0.5rem",
    lg: "0.75rem",
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
  },
};

const AppContainer = styled.div`
  min-height: 100vh;
`;

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function ThemeInitializer({ children }: { children: React.ReactNode }) {
  useTheme();
  return <>{children}</>;
}

function AppContent() {
  const dispatch = useDispatch();
  const { isAuthenticated, token } = useSelector(
    (state: RootState) => state.auth
  );

  const shouldFetchProfile = !!token && isAuthenticated;
  const { error: profileError } = useGetProfileQuery(undefined, {
    skip: !shouldFetchProfile,
  });

  useEffect(() => {
    checkStorageHealth();

    const savedToken = localStorage.getItem("token");
    if (savedToken && !isAuthenticated) {
      dispatch(initializeAuth());
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (profileError && "status" in profileError) {
      if (profileError.status === 401) {
        console.log("❌ Token expired, logging out");
        dispatch(logout());
      } else if (profileError.status === 431) {
        console.error("❌ 431 error in profile fetch - clearing storage");
        localStorage.clear();
        sessionStorage.clear();
        dispatch(logout());
        window.location.reload();
      }
    }
  }, [profileError, dispatch]);

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <Orders />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders/:orderId"
          element={
            <ProtectedRoute>
              <OrderDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/balance"
          element={
            <ProtectedRoute>
              <Balance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/requisites"
          element={
            <ProtectedRoute>
              <NewRequisites />
            </ProtectedRoute>
          }
        />

        <Route
          path="/appeals"
          element={
            <ProtectedRoute>
              <Appeals />
            </ProtectedRoute>
          }
        />
        <Route
          path="/appeals/:id"
          element={
            <ProtectedRoute>
              <AppealDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/device-test"
          element={
            <ProtectedRoute>
              <DeviceTestPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <Router>
          <ThemeInitializer>
            <AppContainer>
              <AppContent />
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: "var(--bg-tooltip)",
                    color: "var(--text-tooltip)",
                    border: "1px solid var(--border-color)",
                  },
                  success: {
                    style: {
                      background: "var(--accent-success)",
                      color: "#ffffff",
                    },
                  },
                  error: {
                    style: {
                      background: "var(--accent-error)",
                      color: "#ffffff",
                    },
                  },
                  loading: {
                    style: {
                      background: "var(--accent-primary)",
                      color: "#ffffff",
                    },
                  },
                }}
              />
            </AppContainer>
          </ThemeInitializer>
        </Router>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
